/**
 * New Year emotes — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearEmotesPack: ToilePack = {
    slug: 'new-year-emotes',
    category: 'season',
    name: 'New Year emotes',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'hype', label: 'Hype hat', subject: 'A round face in a paper party hat with wide excited eyes and confetti bursting around it, built to read tiny, isolated'},
        {key: 'sleepy', label: 'Sleepy midnight', subject: 'A round face with drooping heavy eyes, a nightcap and a small clock striking midnight beside it, built to read tiny, isolated'},
        {key: 'cheers', label: 'Cheers', subject: 'A round face with closed happy eyes raising a champagne glass with a splash of bubbles, built to read tiny, isolated'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, bold clean shapes, no outlines, hard edges', palette: 'Black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers, smooth gradient-free fills', palette: 'Electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space, no outlines, hard edges', palette: 'Gold and white with a single black', framing: 'Centered with generous white space around it on a soft cream background'},
    ],
    directions: ['a dog instead of a face', 'with a countdown number shown', 'in Lunar New Year red and gold'],
    directionHint: 'try: a dog instead of a face',
    seo: {
        title: 'New Year Emotes: Hype Face, Sleepy Face, Cheers',
        metaDescription: 'Nine new year emotes — party-hat hype face, sleepy midnight face, cheers glass — round and expressive, built for chat, three styles.',
        h1: 'New year emotes: hype face, sleepy face, cheers',
        tagline: 'Nine emotes, three midnight subjects, three styles each.',
        intro: 'Small, expressive chat emotes for midnight: a party-hat hype face, a sleepy just-past-midnight face, a cheers glass, each built round and readable at chat size. Ask for a fireworks-eyes reaction or your own expression, and the same three styles carry it.',
        useCases: [
            'a Twitch channel emote set',
            'a Discord server custom emoji',
            'a Slack workspace reaction pack',
            'a sticker pack for messaging apps',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine emotes — a party-hat hype face, a sleepy midnight face, a cheers glass — round and expressive, in three styles.'},
            {q: 'Will these work as Twitch or Discord emotes?', a: "The round, high-contrast style is built for small chat display; check your specific platform's size and format requirements before uploading."},
            {q: 'Are these based on my own face?', a: 'No — these are standalone illustrated emotes, not a likeness generated from a photo of you.'},
            {q: 'Can I get a different expression?', a: 'Yes — the direction field takes any new year reaction, including fireworks-eyes excitement or a confetti-throw pose.'},
            {q: 'Can I sell an emote set made from this?', a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
